import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Play, Pause, Layers, MessageSquare, Send, Loader2, Film, ThumbsUp, MessageCircleWarning } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (s) => {
  s = Math.max(0, s || 0);
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

const toneColor = {
  drama: "#FF2E93", suspense: "#8B5CF6", comédia: "#F59E0B",
  comedia: "#F59E0B", romance: "#00E5FF", treta: "#EF4444",
};

export default function SharedCut() {
  const { token } = useParams();
  const videoRef = useRef(null);
  const pendingSeek = useRef(null);
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeVid, setActiveVid] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [comments, setComments] = useState([]);
  const [author, setAuthor] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [approval, setApproval] = useState(null);
  const [apMode, setApMode] = useState(null);
  const [apComment, setApComment] = useState("");
  const [apSending, setApSending] = useState(false);
  const [apError, setApError] = useState("");

  useEffect(() => {
    axios.get(`${API}/share/${token}`).then((r) => {
      setData(r.data);
      setApproval(r.data.cut.client_approval || null);
      const m = r.data.cut.moments?.[0];
      if (m) setActiveVid(m.video_id);
    }).catch(() => setNotFound(true));
    axios.get(`${API}/share/${token}/comments`).then((r) => setComments(r.data)).catch(() => {});
  }, [token]);

  const sendApproval = async (status) => {
    if (status === "changes_requested" && !apComment.trim()) {
      setApError("Descreva os ajustes desejados antes de enviar.");
      return;
    }
    setApSending(true);
    setApError("");
    try {
      const { data: doc } = await axios.post(`${API}/share/${token}/approval`,
        { status, author, comment: apComment });
      setApproval(doc);
      setApMode(null);
      setApComment("");
      if (doc.comment) {
        axios.get(`${API}/share/${token}/comments`).then((r) => setComments(r.data)).catch(() => {});
      }
    } catch (err) {
      setApError(err.response?.data?.detail || "Não foi possível enviar. Tente novamente.");
    }
    setApSending(false);
  };

  const playMoment = (m) => {
    if (m.video_id === activeVid && videoRef.current) {
      videoRef.current.currentTime = m.start;
      videoRef.current.play();
    } else {
      pendingSeek.current = m.start;
      setActiveVid(m.video_id);
    }
  };

  const sendComment = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data: c } = await axios.post(`${API}/share/${token}/comments`, { author, text });
      setComments([...comments, c]);
      setText("");
    } catch { /* noop */ }
    setSending(false);
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#08080C] text-[#F4F4F5] flex items-center justify-center">
        <p className="text-[#A1A1AA]">Link de prévia inválido ou expirado.</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#08080C] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#FF2E93]" />
      </div>
    );
  }

  const { cut, videos, project_title } = data;
  const hasMedia = videos.some((v) => v.has_media);

  return (
    <div className="min-h-screen bg-[#08080C] text-[#F4F4F5]" data-testid="shared-cut-page">
      <header className="border-b border-[#282834] px-6 py-4">
        <p className="text-[10px] uppercase tracking-widest text-[#FF2E93]">Prévia · {project_title}</p>
        <h1 className="font-head text-xl mt-1 flex items-center gap-2">
          <Layers className="w-5 h-5 text-[#FF2E93]" /> {cut.title}
        </h1>
        {cut.teaser && <p className="text-sm text-[#A1A1AA] italic mt-1 max-w-2xl">&ldquo;{cut.teaser}&rdquo;</p>}
      </header>

      <div className="max-w-5xl mx-auto p-6 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6">
        <div>
          <div className="bg-black rounded-xl overflow-hidden" style={{ aspectRatio: "16/9" }}>
            {hasMedia && activeVid ? (
              <video ref={videoRef} src={`${API}/videos/${activeVid}/stream`} className="w-full h-full object-contain"
                data-testid="shared-player" controls preload="metadata"
                onLoadedMetadata={() => {
                  if (pendingSeek.current != null && videoRef.current) {
                    videoRef.current.currentTime = pendingSeek.current;
                    videoRef.current.play();
                    pendingSeek.current = null;
                  }
                }}
                onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Film className="w-8 h-8 text-[#282834]" />
              </div>
            )}
          </div>
          <div className="mt-4 space-y-2">
            {cut.moments.map((m, i) => (
              <button key={i} onClick={() => playMoment(m)} data-testid={`shared-moment-${i}`}
                className="w-full text-left bg-[#121218] border border-[#282834] hover:border-[#FF2E93] rounded-lg p-3 transition-colors duration-200"
                style={{ borderLeft: `3px solid ${toneColor[m.tone] || "#FF2E93"}` }}>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono text-[#555]">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-sm font-medium flex-1 truncate">{m.title}</span>
                  <span className="text-[10px] font-mono text-[#00E5FF]">{playing ? <Pause className="w-3 h-3 inline" /> : <Play className="w-3 h-3 inline" />} {fmt(m.start)}→{fmt(m.end)}</span>
                </div>
                {m.narration && <p className="text-xs text-[#A1A1AA] mt-1 line-clamp-2">{m.narration}</p>}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div data-testid="approval-box" className="mb-5 bg-[#121218] border border-[#282834] rounded-xl p-4">
            {approval && !apMode ? (
              <div>
                <p className={`text-sm font-semibold flex items-center gap-2 ${approval.status === "approved" ? "text-[#10B981]" : "text-[#F59E0B]"}`}
                  data-testid="approval-status">
                  {approval.status === "approved"
                    ? <><ThumbsUp className="w-4 h-4" /> Episódio aprovado</>
                    : <><MessageCircleWarning className="w-4 h-4" /> Ajustes solicitados</>}
                  <span className="text-[#555] text-xs font-normal">por {approval.author}</span>
                </p>
                {approval.comment && <p className="text-xs text-[#A1A1AA] mt-1">&ldquo;{approval.comment}&rdquo;</p>}
                <button onClick={() => { setApMode("choose"); setApComment(""); }} data-testid="approval-change-button"
                  className="text-[10px] text-[#555] underline underline-offset-2 mt-2 hover:text-[#A1A1AA] transition-colors duration-200">
                  alterar avaliação
                </button>
              </div>
            ) : (
              <div>
                <p className="text-sm font-semibold mb-3">O que achou desta prévia?</p>
                {apMode !== "changes" ? (
                  <div className="flex gap-2">
                    <button onClick={() => sendApproval("approved")} disabled={apSending} data-testid="approve-cut-button"
                      className="flex-1 bg-[#10B981] hover:bg-[#34d399] text-black font-semibold text-sm rounded-lg py-2.5 flex items-center justify-center gap-2 transition-colors duration-200 disabled:opacity-50">
                      {apSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ThumbsUp className="w-4 h-4" />} Aprovar episódio
                    </button>
                    <button onClick={() => { setApMode("changes"); setApError(""); }} data-testid="request-changes-button"
                      className="flex-1 bg-[#1C1C24] border border-[#F59E0B]/50 hover:border-[#F59E0B] text-[#F59E0B] text-sm rounded-lg py-2.5 flex items-center justify-center gap-2 transition-colors duration-200">
                      <MessageCircleWarning className="w-4 h-4" /> Pedir ajustes
                    </button>
                  </div>
                ) : (
                  <div>
                    <textarea value={apComment} onChange={(e) => setApComment(e.target.value)}
                      placeholder="Descreva os ajustes que você gostaria (ex: encurtar a cena 3, trocar a ordem...)"
                      data-testid="changes-comment-input"
                      className="w-full bg-[#08080C] border border-[#282834] rounded-lg px-3 py-2 text-sm h-20 resize-none focus:outline-none focus:border-[#F59E0B] transition-colors duration-200" />
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => sendApproval("changes_requested")} disabled={apSending} data-testid="send-changes-button"
                        className="bg-[#F59E0B] hover:bg-[#fbbf24] text-black font-semibold text-sm rounded-lg px-4 py-2 flex items-center gap-2 transition-colors duration-200 disabled:opacity-50">
                        {apSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Enviar pedido
                      </button>
                      <button onClick={() => setApMode(null)} className="text-xs text-[#555] underline">cancelar</button>
                    </div>
                  </div>
                )}
                {apError && <p className="text-xs text-[#EF4444] mt-2" data-testid="approval-error">{apError}</p>}
              </div>
            )}
          </div>
          <h2 className="text-sm font-semibold flex items-center gap-2 mb-3">
            <MessageSquare className="w-4 h-4 text-[#00E5FF]" /> Comentários da prévia
          </h2>
          <div className="space-y-2 mb-4 max-h-96 overflow-y-auto">
            {comments.length === 0 && <p className="text-xs text-[#555]">Seja o primeiro a comentar esta prévia.</p>}
            {comments.map((c) => (
              <div key={c.id} className="bg-[#121218] border border-[#282834] rounded-lg p-3">
                <p className="text-[10px] text-[#00E5FF]">{c.user_name} <span className="text-[#555]">· {c.user_role}</span></p>
                <p className="text-sm mt-1">{c.text}</p>
              </div>
            ))}
          </div>
          <input value={author} onChange={(e) => setAuthor(e.target.value)} placeholder="Seu nome"
            data-testid="guest-name-input"
            className="w-full bg-[#121218] border border-[#282834] rounded-lg px-3 py-2 text-sm mb-2 focus:outline-none focus:border-[#00E5FF] transition-colors duration-200" />
          <div className="flex gap-2">
            <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Deixe seu feedback sobre o episódio..."
              data-testid="guest-comment-input"
              className="flex-1 bg-[#121218] border border-[#282834] rounded-lg px-3 py-2 text-sm h-20 resize-none focus:outline-none focus:border-[#00E5FF] transition-colors duration-200" />
            <button onClick={sendComment} disabled={sending || !text.trim()} data-testid="guest-comment-send"
              className="self-end bg-[#FF2E93] hover:bg-[#ff54a6] text-white rounded-lg px-4 py-2 transition-colors duration-200 disabled:opacity-40">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
          <p className="text-[10px] text-[#555] mt-4">Prévia gerada pelo Reality Editor Studio. Os cortes são um rascunho de edição — a finalização acontece no NLE.</p>
        </div>
      </div>
    </div>
  );
}
